import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { uploadFile } from '@/lib/utils'

export default function VirtualAttendantSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const { virtualAttendant } = event

  const update = (key: keyof typeof virtualAttendant, value: unknown) => {
    onChange({ virtualAttendant: { ...virtualAttendant, [key]: value } })
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-semibold">{t('editor.virtualAttendant')}</h2>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={virtualAttendant.enabled} onChange={(e) => update('enabled', e.target.checked)} className="h-4 w-4" />
        {t('editor.virtualAttendant')}
      </label>

      {virtualAttendant.enabled && (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium">Start Screen Video</label>
            <input type="file" accept="video/*" onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) {
                const url = await uploadFile(file)
                if (url) update('startScreenVideo', url)
              }
            }} className="block w-full text-sm" />
            {virtualAttendant.startScreenVideo && <div className="text-xs text-muted-foreground">{virtualAttendant.startScreenVideo}</div>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Countdown Video</label>
            <input type="file" accept="video/*" onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) {
                const url = await uploadFile(file)
                if (url) update('countdownVideo', url)
              }
            }} className="block w-full text-sm" />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={virtualAttendant.randomizeBeforeCountdown} onChange={(e) => update('randomizeBeforeCountdown', e.target.checked)} className="h-4 w-4" />
            Randomize Before Countdown Audio
          </label>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('attendant.beforeCountdownAudio')}</label>
            <input type="file" accept="audio/*" multiple onChange={async (e) => {
              const files = Array.from(e.target.files || [])
              const urls: string[] = []
              for (const f of files) {
                const url = await uploadFile(f)
                if (url) urls.push(url)
              }
              if (urls.length > 0) update('beforeCountdownAudio', [...virtualAttendant.beforeCountdownAudio, ...urls])
            }} className="block w-full text-sm" />
            <div className="space-y-1">
              {virtualAttendant.beforeCountdownAudio.map((url, i) => (
                <div key={i} className="flex items-center justify-between rounded bg-muted px-2 py-1 text-xs">
                  <span className="truncate">{url}</span>
                  <button onClick={() => update('beforeCountdownAudio', virtualAttendant.beforeCountdownAudio.filter((_, idx) => idx !== i))} className="text-destructive">{t('common.delete')}</button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('attendant.afterCaptureAudio')}</label>
            <input type="file" accept="audio/*" multiple onChange={async (e) => {
              const files = Array.from(e.target.files || [])
              const urls: string[] = []
              for (const f of files) {
                const url = await uploadFile(f)
                if (url) urls.push(url)
              }
              if (urls.length > 0) update('afterCaptureAudio', [...virtualAttendant.afterCaptureAudio, ...urls])
            }} className="block w-full text-sm" />
            <div className="space-y-1">
              {virtualAttendant.afterCaptureAudio.map((url, i) => (
                <div key={i} className="flex items-center justify-between rounded bg-muted px-2 py-1 text-xs">
                  <span className="truncate">{url}</span>
                  <button onClick={() => update('afterCaptureAudio', virtualAttendant.afterCaptureAudio.filter((_, idx) => idx !== i))} className="text-destructive">{t('common.delete')}</button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
